import React, { useState } from "react";
import {
  createUserWithEmailAndPassword,
  deleteUser,
  isSignInWithEmailLink,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signOut,
  updatePassword,
} from "firebase/auth";
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import QuotationApp from "./components/QuotationApp";
import SuperAdminPanel from "./components/SuperAdminPanel";
import { useAccount } from "./context/AccountContext";
import { auth, db, functions } from "./firebase";
import { getFeatureAccess } from "./utils/featureAccess";
import { trackedGetDoc } from "./utils/firestoreReadCache";
import {
  normalizeEmailValue,
  normalizePermissionSet,
  normalizeTeamMembers,
} from "./utils/teamAccess";
import { IS_DEV_UI_ENABLED } from "./utils/devUi";
import "./App.css";

const featureCards = [
  {
    title: "Fast estimate builder",
    description: "Create polished curtain, blind, and upholstery quotations in minutes with fewer manual steps."
  },
  {
    title: "Fabric-first workflow",
    description: "Keep measurements, lining options, accessories, and pricing logic aligned around real fabric decisions."
  },
  {
    title: "Built for interior teams",
    description: "Help showrooms, workshops, and sales teams quote consistently without spreadsheets slowing them down."
  }
];

const workflowSteps = [
  "Capture room measurements, fabric selections, and finishing details in one place.",
  "Generate client-ready pricing with cleaner structure and fewer back-and-forth corrections.",
  "Move from enquiry to approved quotation with a workflow your team can actually repeat."
];

const trustStats = [
  { value: "10x", label: "quicker quoting rhythm" },
  { value: "1", label: "shared source of pricing truth" },
  { value: "0", label: "spreadsheet chaos required" }
];

function emailInviteKey(value = "") {
  return normalizeEmailValue(value).replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function App() {
  const {
    account,
    accountUser,
    loading: accountLoading,
    hasActiveSubscription,
    isSuperAdmin,
    error: accountError,
    debug: accountDebug,
    refreshAccount,
    subscriptionStatus,
    trialQuotationLimit,
    trialQuotationsUsed,
  } = useAccount();
  const [user, setUser] = useState(null);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signupName, setSignupName] = useState("");
  const [signupBusinessName, setSignupBusinessName] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupCity, setSignupCity] = useState("");
  const [signupBusinessType, setSignupBusinessType] = useState("");
  const [signupStaffCount, setSignupStaffCount] = useState("");
  const [signupMonthlyVolume, setSignupMonthlyVolume] = useState("");
  const [signupGstNumber, setSignupGstNumber] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRequestingActivation, setIsRequestingActivation] = useState(false);
  const [activationNotice, setActivationNotice] = useState("");
  const [inviteResolution, setInviteResolution] = useState({
    processing: false,
    pending: null,
    error: "",
  });
  const [invitePassword, setInvitePassword] = useState("");
  const [invitePasswordConfirm, setInvitePasswordConfirm] = useState("");
  const [isCompletingInvite, setIsCompletingInvite] = useState(false);
  const [accountLoadTimedOut, setAccountLoadTimedOut] = useState(false);

  React.useEffect(() => {
    if (IS_DEV_UI_ENABLED) {
      console.log("APP_STARTUP_TRACE", { step: "auth-state-start" });
    }
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (IS_DEV_UI_ENABLED) {
        console.log("APP_STARTUP_TRACE", {
          step: "auth-state-resolved",
          uid: currentUser?.uid || "",
          email: currentUser?.email || "",
        });
      }
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  React.useEffect(() => {
    if (!user || !accountLoading) {
      setAccountLoadTimedOut(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      console.error("FABRIQUOTE_ACCOUNT_LOAD_TIMEOUT", {
        step: "workspace-load-failed",
        errorCode: "account_load_timeout",
        errorMessage: "Account/workspace loading exceeded 15 seconds.",
        authUid: user.uid || "",
        email: user.email || "",
        workspaceUid: accountUser?.accountId || account?.id || "",
      });
      setAccountLoadTimedOut(true);
    }, 15000);

    return () => window.clearTimeout(timeoutId);
  }, [account?.id, accountLoading, accountUser?.accountId, user]);

  React.useEffect(() => {
    let active = true;

    const resolveInviteLink = async () => {
      if (!isSignInWithEmailLink(auth, window.location.href)) {
        return;
      }

      setInviteResolution({
        processing: true,
        pending: null,
        error: "",
      });

      try {
        const inviteUrl = new URL(window.location.href);
        const inviteId =
          inviteUrl.searchParams.get("inviteId") ||
          inviteUrl.searchParams.get("invite");
        const hasLegacyWorkspaceParam =
          inviteUrl.searchParams.has("workspaceId") ||
          inviteUrl.searchParams.has("workspace");
        const workspaceId =
          inviteUrl.searchParams.get("workspaceId") ||
          inviteUrl.searchParams.get("workspace") ||
          inviteUrl.searchParams.get("accountId");
        const accountIdParam = inviteUrl.searchParams.get("accountId") || "";
        const isAccountInvite =
          inviteUrl.searchParams.get("accountInvite") === "1" ||
          Boolean(accountIdParam && inviteUrl.searchParams.get("inviteId") && !hasLegacyWorkspaceParam);
        const accountInviteAccountId =
          accountIdParam || inviteUrl.searchParams.get("account") || "";
        const accountInviteId =
          inviteUrl.searchParams.get("inviteId") ||
          inviteUrl.searchParams.get("invite") ||
          "";
        const inviteEmail =
          inviteUrl.searchParams.get("email") ||
          window.localStorage.getItem("fabriquote-invite-email") ||
          "";

        if (isAccountInvite) {
          if (!inviteEmail) {
            throw new Error("This account invite link is missing an email address.");
          }

          const credential = await signInWithEmailLink(
            auth,
            inviteEmail,
            window.location.href
          );
          const resolvedEmail = normalizeEmailValue(
            credential.user?.email || inviteEmail
          );
          const pendingKey = emailInviteKey(resolvedEmail);
          let pendingData = null;
          let resolvedAccountInviteId = accountInviteId;

          if (accountInviteAccountId && accountInviteId) {
            const accountInviteRef = doc(
              db,
              "accounts",
              accountInviteAccountId,
              "invites",
              accountInviteId
            );
            const accountInviteSnap = await trackedGetDoc(accountInviteRef, {
              label: "resolveAccountInviteDoc",
              cacheKey: `accounts/${accountInviteAccountId}/invites/${accountInviteId}`,
            });
            if (accountInviteSnap.exists()) {
              pendingData = {
                inviteId: accountInviteSnap.id,
                accountId: accountInviteAccountId,
                ...(accountInviteSnap.data() || {}),
              };
              resolvedAccountInviteId = accountInviteSnap.id;
            }
          }

          const pendingRef = doc(db, "pendingAccountInvites", pendingKey);
          const pendingSnap = pendingData
            ? null
            : await trackedGetDoc(pendingRef, {
                label: "resolveAccountInviteLink",
                cacheKey: `pendingAccountInvites/${pendingKey}`,
              });

          if (!pendingData && !pendingSnap.exists()) {
            throw new Error("This account invite could not be found.");
          }

          pendingData = pendingData || pendingSnap.data() || {};
          if (
            normalizeEmailValue(pendingData.email || "") &&
            normalizeEmailValue(pendingData.email || "") !== resolvedEmail
          ) {
            throw new Error("This account invite email does not match the signed-in user.");
          }

          window.localStorage.removeItem("fabriquote-invite-email");
          window.history.replaceState({}, document.title, window.location.pathname);

          if (!active) return;

          setInviteResolution({
            processing: false,
            pending: {
              type: "account",
              pendingKey,
              accountInviteId: resolvedAccountInviteId,
              pendingData,
              email: credential.user?.email || inviteEmail,
            },
            error: "",
          });
          return;
        }

        if (!inviteId || !workspaceId || !inviteEmail) {
          throw new Error("This invite link is incomplete.");
        }

        const credential = await signInWithEmailLink(
          auth,
          inviteEmail,
          window.location.href
        );

        const inviteRef = doc(db, "users", workspaceId, "team_invites", inviteId);
        const inviteSnap = await trackedGetDoc(inviteRef, {
          label: "resolveTeamInviteLink",
          cacheKey: `users/${workspaceId}/team_invites/${inviteId}`,
        });

        if (!inviteSnap.exists()) {
          throw new Error("This invite could not be found.");
        }

        const inviteData = inviteSnap.data() || {};
        const resolvedEmail = normalizeEmailValue(
          credential.user?.email || inviteEmail
        );

        if (
          normalizeEmailValue(inviteData.email || "") &&
          normalizeEmailValue(inviteData.email || "") !== resolvedEmail
        ) {
          throw new Error("This invite email does not match the signed-in user.");
        }

        window.localStorage.removeItem("fabriquote-invite-email");
        window.history.replaceState({}, document.title, window.location.pathname);

        if (!active) return;

        setInviteResolution({
          processing: false,
          pending: {
            inviteId,
            workspaceId,
            inviteData,
            email: credential.user?.email || inviteEmail,
          },
          error: "",
        });
      } catch (inviteError) {
        if (!active) return;

        setInviteResolution({
          processing: false,
          pending: null,
          error:
            inviteError?.message ||
            "We couldn't accept this team invite. Please contact the workspace owner.",
        });
      }
    };

    resolveInviteLink();

    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        const credential = await createUserWithEmailAndPassword(auth, email, password);
        try {
          const registerTrialWorkspace = httpsCallable(functions, "registerTrialWorkspace");
          await registerTrialWorkspace({
            fullName: signupName,
            businessName: signupBusinessName,
            email: credential.user.email || email,
            phone: signupPhone,
            city: signupCity,
            businessType: signupBusinessType,
            staffCount: signupStaffCount,
            monthlyQuoteVolume: signupMonthlyVolume,
            gstNumber: signupGstNumber,
          });
        } catch (trialError) {
          try {
            await deleteUser(credential.user);
          } catch (cleanupError) {
            if (IS_DEV_UI_ENABLED) {
              console.error("SIGNUP_TRIAL_REGISTRATION_CLEANUP_FAILED", cleanupError);
            }
          }
          throw trialError;
        }
        await refreshAccount();
      }
    } catch (submitError) {
      setError(submitError?.message?.replace(/^functions\//, "") || (
        isLogin
          ? "We couldn't sign you in. Please check your email and password."
          : "We couldn't create your account. Make sure all required fields are filled and your password has at least 6 characters."
      ));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestActivation = async () => {
    if (!account?.id || !user) return;
    setIsRequestingActivation(true);
    setActivationNotice("");
    setError("");

    try {
      const submitSalesActivationRequest = httpsCallable(functions, "submitSalesActivationRequest");
      await submitSalesActivationRequest({
        accountId: account.id,
        businessName: account.name || signupBusinessName || "",
        name: accountUser?.displayName || user.displayName || signupName || "",
        email: user.email || email,
        phone: account?.phone || signupPhone || "",
      });
      setActivationNotice("Activation request sent. Our team will contact you shortly.");
      await refreshAccount();
    } catch (requestError) {
      setError(requestError?.message || "We could not send your activation request right now.");
    } finally {
      setIsRequestingActivation(false);
    }
  };

  const salesPhone = process.env.REACT_APP_SALES_PHONE || "";
  const salesWhatsapp = process.env.REACT_APP_SALES_WHATSAPP || salesPhone;
  const salesEmail = process.env.REACT_APP_SALES_EMAIL || "";
  const whatsappMessage = encodeURIComponent(
    `Hi FabriQuote team, my 10 free quotations are complete. I would like to activate my account.\nBusiness: ${
      account?.name || signupBusinessName || "-"
    }\nEmail: ${user?.email || email || "-"}\nWorkspace: ${account?.id || "-"}`
  );
  const whatsappHref = salesWhatsapp
    ? `https://wa.me/${String(salesWhatsapp).replace(/\D+/g, "")}?text=${whatsappMessage}`
    : "";
  const callHref = salesPhone ? `tel:${salesPhone}` : "";

  const completeInviteSetup = async (event) => {
    event.preventDefault();

    if (!inviteResolution.pending || !auth.currentUser) {
      return;
    }

    if (invitePassword.length < 6) {
      setInviteResolution((prev) => ({
        ...prev,
        error: "Please set a password with at least 6 characters.",
      }));
      return;
    }

    if (invitePassword !== invitePasswordConfirm) {
      setInviteResolution((prev) => ({
        ...prev,
        error: "The password confirmation does not match.",
      }));
      return;
    }

    setIsCompletingInvite(true);
    setInviteResolution((prev) => ({ ...prev, error: "" }));

    try {
      await updatePassword(auth.currentUser, invitePassword);

      if (inviteResolution.pending.type === "account") {
        const { pendingKey, pendingData, email: invitedEmail, accountInviteId } =
          inviteResolution.pending;
        const accountId = String(pendingData.accountId || "");
        if (!accountId) {
          throw new Error("This account invite is missing its account assignment.");
        }

        const role = pendingData.role || "staff";
        const plan = pendingData.plan || "full";
        const features = pendingData.features || getFeatureAccess(plan);
        const normalizedEmail = normalizeEmailValue(auth.currentUser.email || invitedEmail);

        await runTransaction(db, async (transaction) => {
          const pendingRef = doc(db, "pendingAccountInvites", pendingKey);
          const userRef = doc(db, "users", auth.currentUser.uid);
          const profileRef = doc(db, "users", auth.currentUser.uid, "profile", "main");
          const accountMemberRef = doc(
            db,
            "accounts",
            accountId,
            "members",
            auth.currentUser.uid
          );
          const accountInviteRef = doc(
            db,
            "accounts",
            accountId,
            "invites",
            accountInviteId || pendingData.inviteId || pendingKey
          );

          transaction.set(
            userRef,
            {
              accountId,
              activeAccountId: accountId,
              role,
              plan,
              isActive: pendingData.isActive !== false,
              email: normalizedEmail,
              displayName: pendingData.name || auth.currentUser.displayName || "",
              updatedAt: serverTimestamp(),
              createdAt: serverTimestamp(),
            },
            { merge: true }
          );

          transaction.set(
            accountMemberRef,
            {
              uid: auth.currentUser.uid,
              email: normalizedEmail,
              name: pendingData.name || "",
              role,
              plan,
              isActive: true,
              features,
              addedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );

          transaction.set(
            profileRef,
            {
              workspaceId: accountId,
              ownerUid: accountId,
              role: role === "viewer" ? "staff" : role,
              status: "active",
              email: normalizedEmail,
              name: pendingData.name || "",
              updatedAt: serverTimestamp(),
              createdAt: serverTimestamp(),
            },
            { merge: true }
          );

          transaction.delete(pendingRef);
          transaction.set(
            accountInviteRef,
            {
              status: "accepted",
              acceptedAt: serverTimestamp(),
              acceptedByUid: auth.currentUser.uid,
              acceptedEmail: normalizedEmail,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        });

        setInvitePassword("");
        setInvitePasswordConfirm("");
        setInviteResolution({
          processing: false,
          pending: null,
          error: "",
        });
        await refreshAccount();
        return;
      }

      const { inviteId, workspaceId, inviteData, email: invitedEmail } =
        inviteResolution.pending;
      const memberId = inviteData.memberId || inviteId;
      const normalizedPermissions = normalizePermissionSet(
        inviteData.permissions || {}
      );
      const invitePlan = inviteData.plan || "full";
      const inviteFeatures = inviteData.features || getFeatureAccess(invitePlan);

      await runTransaction(db, async (transaction) => {
        const settingsRef = doc(db, "users", workspaceId, "settings", "main");
        const inviteRef = doc(db, "users", workspaceId, "team_invites", inviteId);
        const profileRef = doc(db, "users", auth.currentUser.uid, "profile", "main");
        const userRef = doc(db, "users", auth.currentUser.uid);
        const accountMemberRef = doc(
          db,
          "accounts",
          workspaceId,
          "members",
          auth.currentUser.uid
        );

        const settingsSnap = await transaction.get(settingsRef);
        const currentMembers = normalizeTeamMembers(
          settingsSnap.exists() ? settingsSnap.data()?.teamMembers : []
        );

        const nextMembers = currentMembers.map((member) =>
          member.id === memberId ||
          member.inviteId === inviteId ||
          normalizeEmailValue(member.email) === normalizeEmailValue(invitedEmail)
            ? {
                ...member,
                uid: auth.currentUser.uid,
                email: auth.currentUser.email || invitedEmail,
                name: inviteData.name || member.name,
                role: inviteData.role || member.role,
                workspaceId,
                permissions: normalizedPermissions,
                status: "active",
                isActive: true,
                acceptedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              }
            : member
        );

        transaction.set(
          settingsRef,
          {
            teamMembers: nextMembers,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        transaction.set(
          inviteRef,
          {
            status: "accepted",
            acceptedAt: serverTimestamp(),
            acceptedByUid: auth.currentUser.uid,
            acceptedEmail: auth.currentUser.email || invitedEmail,
          },
          { merge: true }
        );

        transaction.set(
          profileRef,
          {
            workspaceId,
            ownerUid: workspaceId,
            memberId,
            role: "staff",
            status: "active",
            permissions: normalizedPermissions,
            email: auth.currentUser.email || invitedEmail,
            name: inviteData.name || "",
            inviteId,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );

        transaction.set(
          userRef,
          {
            accountId: workspaceId,
            activeAccountId: workspaceId,
            role: inviteData.role || "staff",
            plan: invitePlan,
            isActive: true,
            email: auth.currentUser.email || invitedEmail,
            displayName: inviteData.name || "",
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );

        transaction.set(
          accountMemberRef,
          {
            uid: auth.currentUser.uid,
            email: auth.currentUser.email || invitedEmail,
            role: inviteData.role || "staff",
            plan: invitePlan,
            isActive: true,
            features: inviteFeatures,
            addedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      });

      setInvitePassword("");
      setInvitePasswordConfirm("");
      setInviteResolution({
        processing: false,
        pending: null,
        error: "",
      });
    } catch (inviteError) {
      setInviteResolution((prev) => ({
        ...prev,
        error:
          inviteError?.message ||
          "We couldn't finish your team account setup. Please try again.",
      }));
    } finally {
      setIsCompletingInvite(false);
    }
  };

  if (inviteResolution.processing) {
    return (
      <main className="site-shell">
        <section className="hero-section">
          <aside className="access-panel" id="access-panel">
            <p className="panel-kicker">Setting up your workspace</p>
            <h2>Accepting your FabriQuote invite</h2>
            <p className="panel-text">
              We&apos;re securely connecting your login to your company workspace.
            </p>
          </aside>
        </section>
      </main>
    );
  }

  if (user && inviteResolution.pending) {
    return (
      <main className="site-shell">
        <section className="hero-section">
          <aside className="access-panel" id="access-panel">
            <p className="panel-kicker">Final step</p>
            <h2>Set your password</h2>
            <p className="panel-text">
              Your FabriQuote invite is active. Create a password for future sign-ins
              to your shared workspace.
            </p>

            <form className="auth-form" onSubmit={completeInviteSetup}>
              <label>
                Password
                <input
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={invitePassword}
                  onChange={(event) => setInvitePassword(event.target.value)}
                  required
                />
              </label>

              <label>
                Confirm password
                <input
                  type="password"
                  placeholder="Repeat your password"
                  value={invitePasswordConfirm}
                  onChange={(event) => setInvitePasswordConfirm(event.target.value)}
                  required
                />
              </label>

              {inviteResolution.error ? (
                <p className="form-error">{inviteResolution.error}</p>
              ) : null}

              <button className="submit-button" type="submit" disabled={isCompletingInvite}>
                {isCompletingInvite ? "Finishing setup..." : "Set password and continue"}
              </button>
            </form>
          </aside>
        </section>
      </main>
    );
  }

  if (user) {
    const currentPath = window.location.pathname;

    if (currentPath.startsWith("/super-admin")) {
      return <SuperAdminPanel />;
    }

    if (accountLoading && accountLoadTimedOut) {
      const timeoutDebug = {
        authUid: user?.uid || "",
        email: user?.email || "",
        workspaceUid: accountUser?.accountId || account?.id || "",
        loadingStep: "account-load-timeout",
        lastCompletedStep: "auth-state-resolved",
        errorCode: "account_load_timeout",
        errorMessage: "Account/workspace loading exceeded 15 seconds.",
      };
      return (
        <main className="site-shell">
          <section className="hero-section">
            <aside className="access-panel" id="access-panel">
              <p className="panel-kicker">Workspace load issue</p>
              <h2>We could not load your workspace</h2>
              <p className="panel-text">
                Please refresh and try again. If this continues, contact support.
              </p>
              <details
                style={{
                  textAlign: "left",
                  background: "rgba(15,23,42,0.06)",
                  border: "1px solid rgba(15,23,42,0.12)",
                  borderRadius: 14,
                  padding: 14,
                  fontSize: 12,
                  lineHeight: 1.6,
                  color: "#334155",
                }}
              >
                <summary style={{ cursor: "pointer", fontWeight: 800 }}>
                  Startup debug
                </summary>
                <pre style={{ whiteSpace: "pre-wrap", margin: "10px 0 0" }}>
                  {JSON.stringify(timeoutDebug, null, 2)}
                </pre>
              </details>
              <button className="submit-button" type="button" onClick={() => window.location.reload()}>
                Refresh
              </button>
              <button className="ghost-button" type="button" onClick={() => signOut(auth)}>
                Sign out
              </button>
            </aside>
          </section>
        </main>
      );
    }

    if (accountLoading) {
      return (
        <main className="site-shell">
          <section className="hero-section">
            <aside className="access-panel" id="access-panel">
              <p className="panel-kicker">Loading account access</p>
              <h2>Preparing your FabriQuote workspace</h2>
              <p className="panel-text">
                We&apos;re checking your account, subscription, and enabled features.
              </p>
            </aside>
          </section>
        </main>
      );
    }

    if (accountError) {
      const showAccountDebug = IS_DEV_UI_ENABLED;
      return (
        <main className="site-shell">
          <section className="hero-section">
            <aside className="access-panel" id="access-panel">
              <p className="panel-kicker">Account access</p>
              <h2>We could not load your account</h2>
              <p className="panel-text">{accountError}</p>
              {accountDebug && showAccountDebug ? (
                <details
                  style={{
                    textAlign: "left",
                    background: "rgba(15,23,42,0.06)",
                    border: "1px solid rgba(15,23,42,0.12)",
                    borderRadius: 14,
                    padding: 14,
                    fontSize: 12,
                    lineHeight: 1.6,
                    color: "#334155",
                  }}
                >
                  <summary style={{ cursor: "pointer", fontWeight: 800 }}>
                    Account load debug
                  </summary>
                  <pre style={{ whiteSpace: "pre-wrap", margin: "10px 0 0" }}>
                    {JSON.stringify(accountDebug, null, 2)}
                  </pre>
                </details>
              ) : null}
              <button className="submit-button" type="button" onClick={refreshAccount}>
                Try again
              </button>
            </aside>
          </section>
        </main>
      );
    }

    if (!hasActiveSubscription && !isSuperAdmin) {
      return (
        <main className="site-shell">
          <section className="hero-section">
            <aside className="access-panel" id="access-panel">
              <p className="panel-kicker">Account status {subscriptionStatus || "inactive"}</p>
              <h2>
                {subscriptionStatus === "trial_exhausted"
                  ? "Your 10 free quotations are complete"
                  : subscriptionStatus === "payment_pending"
                  ? "Your activation request is pending"
                  : "Your FabriQuote workspace is currently locked"}
              </h2>
              <p className="panel-text">
                {subscriptionStatus === "trial_exhausted"
                  ? "To continue using FabriQuote, please contact our sales team and activate your account."
                  : subscriptionStatus === "payment_pending"
                  ? "Our team will contact you shortly to complete activation."
                  : subscriptionStatus === "suspended"
                  ? "Your account is currently inactive. Please contact support."
                  : "This account is currently locked. Please contact FabriQuote support to restore access."}
              </p>
              <div className="locked-summary">
                <div><strong>{trialQuotationsUsed}</strong><span>used</span></div>
                <div><strong>{trialQuotationLimit}</strong><span>limit</span></div>
                <div><strong>{account?.name || "Workspace"}</strong><span>business</span></div>
              </div>
              {activationNotice ? <p className="form-success">{activationNotice}</p> : null}
              {error ? <p className="form-error">{error}</p> : null}
              <div className="locked-actions">
                {whatsappHref ? (
                  <a className="primary-button" href={whatsappHref} target="_blank" rel="noreferrer">
                    Contact on WhatsApp
                  </a>
                ) : null}
                {callHref ? (
                  <a className="ghost-button locked-link-button" href={callHref}>
                    Call Sales
                  </a>
                ) : null}
                <button
                  className="submit-button"
                  type="button"
                  onClick={handleRequestActivation}
                  disabled={isRequestingActivation}
                >
                  {isRequestingActivation ? "Sending request..." : "Request Callback"}
                </button>
                <button className="ghost-button locked-link-button" type="button" onClick={() => signOut(auth)}>
                  Logout
                </button>
              </div>
              {salesEmail ? <p className="panel-support">Sales email: {salesEmail}</p> : null}
            </aside>
          </section>
        </main>
      );
    }

    return <QuotationApp />;
  }

  return (
    <main className="site-shell">
      <section className="hero-section">
        <header className="site-header">
          <div className="brand-lockup">
            <img className="brand-mark" src={`${process.env.PUBLIC_URL}/logo.png`} alt="Fabriquote logo" />
            <div>
              <p className="brand-name">Fabriquote</p>
              <p className="brand-subtitle">Quotation software for soft furnishings</p>
            </div>
          </div>
          <button className="ghost-button" type="button" onClick={() => setIsLogin((current) => !current)}>
            {isLogin ? "Create account" : "Back to login"}
          </button>
        </header>

        <div className="hero-grid">
          <div className="hero-copy">
            <p className="eyebrow">For curtains, blinds, upholstery, and interior projects</p>
            <h1>Quotations that feel as refined as the spaces you design.</h1>
            <p className="hero-text">
              Fabriquote helps interior businesses turn fabric selections, measurements, and finishing details into
              clean, client-ready quotations without the spreadsheet mess.
            </p>

            <div className="hero-actions">
              <a className="primary-button" href="#access-panel">
                Start with Fabriquote
              </a>
              <a className="text-link" href="#workflow">
                See how it works
              </a>
            </div>

            <div className="stats-row" aria-label="Fabriquote value highlights">
              {trustStats.map((stat) => (
                <article className="stat-card" key={stat.label}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </article>
              ))}
            </div>
          </div>

          <aside className="access-panel" id="access-panel">
            <p className="panel-kicker">{isLogin ? "Welcome back" : "Open your workspace"}</p>
            <h2>{isLogin ? "Sign in to continue quoting" : "Start your free trial"}</h2>
            <p className="panel-text">
              {isLogin
                ? "Access your existing quotation workflow and pick up where you left off."
                : "Create your workspace, get 10 free quotations, and start testing FabriQuote on your real furnishing workflow."}
            </p>

            {!isLogin ? (
              <div className="trial-highlight">
                <strong>10 free quotations included</strong>
                <span>No payment gateway required. Activate later through the FabriQuote sales team.</span>
              </div>
            ) : null}

            <form className="auth-form" onSubmit={handleSubmit}>
              {!isLogin ? (
                <>
                  <label>
                    Full name
                    <input
                      type="text"
                      placeholder="Your name"
                      value={signupName}
                      onChange={(event) => setSignupName(event.target.value)}
                      required={!isLogin}
                    />
                  </label>
                  <label>
                    Business name
                    <input
                      type="text"
                      placeholder="FabriQuote Studio"
                      value={signupBusinessName}
                      onChange={(event) => setSignupBusinessName(event.target.value)}
                      required={!isLogin}
                    />
                  </label>
                </>
              ) : null}
              <label>
                Email address
                <input
                  type="email"
                  placeholder="studio@fabriquote.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </label>

              {!isLogin ? (
                <>
                  <label>
                    Phone number
                    <input
                      type="tel"
                      placeholder="+91 98xxxxxxx"
                      value={signupPhone}
                      onChange={(event) => setSignupPhone(event.target.value)}
                      required={!isLogin}
                    />
                  </label>
                  <label>
                    City / location
                    <input
                      type="text"
                      placeholder="Kochi"
                      value={signupCity}
                      onChange={(event) => setSignupCity(event.target.value)}
                      required={!isLogin}
                    />
                  </label>
                </>
              ) : null}

              <label>
                Password
                <input
                  type="password"
                  placeholder={isLogin ? "Enter your password" : "Minimum 6 characters"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </label>

              {!isLogin ? (
                <div className="auth-grid-two">
                  <label>
                    Business type
                    <input
                      type="text"
                      placeholder="Curtains, sofas, blinds..."
                      value={signupBusinessType}
                      onChange={(event) => setSignupBusinessType(event.target.value)}
                    />
                  </label>
                  <label>
                    Number of staff
                    <input
                      type="text"
                      placeholder="5"
                      value={signupStaffCount}
                      onChange={(event) => setSignupStaffCount(event.target.value)}
                    />
                  </label>
                  <label>
                    Monthly quote volume
                    <input
                      type="text"
                      placeholder="40 quotes / month"
                      value={signupMonthlyVolume}
                      onChange={(event) => setSignupMonthlyVolume(event.target.value)}
                    />
                  </label>
                  <label>
                    GST number
                    <input
                      type="text"
                      placeholder="Optional"
                      value={signupGstNumber}
                      onChange={(event) => setSignupGstNumber(event.target.value)}
                    />
                  </label>
                </div>
              ) : null}

              {error && <p className="form-error">{error}</p>}
              {inviteResolution.error && !user ? (
                <p className="form-error">{inviteResolution.error}</p>
              ) : null}

              <button className="submit-button" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Please wait..." : isLogin ? "Sign in" : "Get 10 free quotations"}
              </button>
            </form>

            <p className="panel-switch">
              {isLogin ? "New here?" : "Already have an account?"}{" "}
              <button className="inline-switch" type="button" onClick={() => setIsLogin((current) => !current)}>
                {isLogin ? "Create an account" : "Sign in instead"}
              </button>
            </p>
          </aside>
        </div>
      </section>

      <section className="feature-section">
        <div className="section-heading">
          <p className="eyebrow">Why teams use it</p>
          <h2>Made for detail-heavy furnishing quotes</h2>
        </div>
        <div className="feature-grid">
          {featureCards.map((feature) => (
            <article className="feature-card" key={feature.title}>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="workflow-section" id="workflow">
        <div className="section-heading">
          <p className="eyebrow">How it flows</p>
          <h2>From fabric choice to final quotation</h2>
        </div>
        <div className="workflow-list">
          {workflowSteps.map((step, index) => (
            <article className="workflow-card" key={step}>
              <span>{`0${index + 1}`}</span>
              <p>{step}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="showcase-section">
        <div className="showcase-copy">
          <p className="eyebrow">A calmer sales process</p>
          <h2>Give your clients clarity without sacrificing the nuance behind the numbers.</h2>
          <p>
            Fabriquote is designed for businesses that sell beautifully considered interiors and need their quoting
            experience to feel just as intentional.
          </p>
        </div>

        <div className="material-board" aria-hidden="true">
          <div className="swatch swatch-sand" />
          <div className="swatch swatch-ink" />
          <div className="swatch swatch-copper" />
          <div className="swatch swatch-linen" />
        </div>
      </section>
    </main>
  );
}

export default App;
